import { Component, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { SharedDataService } from '../../services/shared-data.service';
import { LabelService } from '../../services/label.service';
import { Dropdown, LabelTypes } from '../../shared/label.types';
import { OutsideClickDirective } from '../../directives/outside-click.directive';
import { ButtonComponent } from '../button/button.component';
import * as Types from '../../shared/types';
import { CommonService } from '../../services/common.service';
import { ManageColumnCount } from '../../shared/constant';
import { formatToUpdateUserColumnPreference, transformColumnPreferences } from '../../shared/appUtil';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'manage-columns',
  standalone: true,
  imports: [CommonModule, FormsModule, OutsideClickDirective, ButtonComponent],
  templateUrl: './manage-columns.component.html',
  styleUrl: './manage-columns.component.scss',
})
export class ManageColumnsComponent {
  constructor(
    private sharedDataService: SharedDataService,
    private labelService: LabelService,
    private commonService: CommonService,
    public notificationService: NotificationService
  ) {}

  @Input() buttonTitle: string = '';
  protected _dropdownOpened = signal<boolean>(false);
  protected _listChanged = signal<boolean>(false);
  filterGroup: Types.FilterGroup = {} as Types.FilterGroup;
  searchQuery: string = '';
  dropdown: Dropdown = {} as Dropdown;
  manageColumnCount = ManageColumnCount;

  ngOnInit() {
    this.labelService.getLabelData().subscribe((data: LabelTypes) => {
      this.dropdown = data?.dropdown ?? {};
    });
    this.filterGroup = this.sharedDataService.getColumnsFilterGroup();

    this.sharedDataService.getToggleValues().subscribe((values) => {
      const opening = values.hideFields && !this._dropdownOpened();
      this._dropdownOpened.set(values.hideFields);
      if (opening) {
        this.sharedDataService.syncFilterGroupFromSelectedColumns();
      }
      if (this._listChanged()) {
        this._apiCall();
      }
    });
  }

  private _apiCall() {
    this.commonService
      .updateUserColumnPreference({ preferences: formatToUpdateUserColumnPreference(this.filterGroup.filters.options) })
      .subscribe((response) => {
        this.notificationService.success('Column Preference', 'Column Preference saved');
        this.fetchUpdatedPreferences();
      });

    if (this._listChanged()) {
      this._listChanged.set(false);
    }
  }

  private fetchUpdatedPreferences() {
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        this.sharedDataService.setColumnNames(transformColumnPreferences(names));
      },
      error: (error) => {
        console.error('Error fetching updated preferences:', error);
      }
    });
  }

  toggleDropdown = () => {
    const willOpen = !this._dropdownOpened();
    if (willOpen) {
      this.sharedDataService.syncFilterGroupFromSelectedColumns();
    }
    this.sharedDataService.toggleHide(willOpen);
  };

  stopPropagation = (event: MouseEvent) => {
    event?.stopPropagation();
  };

  checkBoxClicked = (event: MouseEvent, item: Types.FilterOption, selected = false) => {
    event?.stopPropagation();

    if (selected) {
      item.isSelected = false;
      item.tempIsSelected = false;
    } else {
      item.isSelected = true;
      item.tempIsSelected = true;
    }
    this._listChanged.set(true);
    this.sharedDataService.handlePopulateFilteredData();
  };

  getColumnList = (selected = false): Array<Types.FilterOption> => {
    const options = this.filterGroup?.filters?.options ?? [];
    const isSelected = (opt: Types.FilterOption) => opt.tempIsSelected ?? opt.isSelected ?? false;
    if (this.searchQuery.length) {
      return options.filter(
        (item) =>
          item.name.toLowerCase().includes(this.searchQuery.toLowerCase()) && isSelected(item) === selected
      );
    }
    return options.filter((i) => isSelected(i) === selected);
  };

  getColumnCount = () => {
    const options = this.filterGroup?.filters?.options ?? [];
    return options.filter((item) => item.tempIsSelected ?? item.isSelected ?? false).length;
  };

  handleRestore = (event: MouseEvent) => {
    this.commonService.resetUserColumnPreference({}).subscribe((preferenceArray) => {
      this.sharedDataService.setColumnNames(transformColumnPreferences(preferenceArray));
      this.sharedDataService.forceUpdateSelectedColumns();
      this.notificationService.success('Column Preference', 'Column Preference restored');
    });
  };

  onOutsideClick = () => {
    this.sharedDataService.toggleHide(false);
  };

  clearSearch() {
    this.searchQuery = '';
  }
}
